import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'

/**
 * Shared validator logic for the Truck and Driver selects on
 * the Truck Gate‑In form.
 *
 * Child classes must implement:
 *   • buildValidateUrl(id) -> string
 */
export default class extends Controller {
  static values  = {
    verifyBol: Boolean,
    required: {
      type: Boolean,
      default: true
    }, // Trucks are always required, drivers are conditionally required.
  }

  connect() {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .content

    this.requestHeaders = {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken,
    }

    this.errors = this.#initialErrors()
    this.#render()
  }

  lockSubmission(key) {
    if (this.hasTruckGateInFormManagerOutlet) {
      this.truckGateInFormManagerOutlet.lockSubmission(key)
    }
  }

  unlockSubmission(key) {
    if (this.hasTruckGateInFormManagerOutlet) {
      this.truckGateInFormManagerOutlet.unlockSubmission(key)
    }
  }

  /**
   * Public helper—call this from the child controller’s `handle…Change` method.
   */
  async validateSelection(id) {
    if (!id || isNaN(id)) {
      this.clearErrors()
      this.#refreshPanelChecks()
      return
    }

    await this.#validateRemote(id)
    this.#refreshPanelChecks()
  }

  /* ------------------------------------------------------------------
   * Internal helpers
   * ---------------------------------------------------------------- */

  async #validateRemote(id) {
    try {
      const url = this.buildValidateUrl?.(id)
      if (!url) return

      const response = await fetch(url, { headers: this.requestHeaders })
      const data     = await response.json()

      if (data.errors?.length) {
        this.setRemoteErrors(data.errors)
      } else {
        this.setRemoteErrors([])
      }
    } catch (err) {
      console.error(`${this.identifier} validation error:`, err)
    }
  }

  /**
   * After validating truck/driver we need to rerun the booking + B/L checks
   * on every Gate‑In panel (full + empty).  We do that via Stimulus outlets.
   */
  #refreshPanelChecks() {
    if (!this.hasTruckGateInValidationsOutlet) return
    const panels = this.truckGateInValidationsOutlets
    panels.forEach((panel) => {
      panel.checkBookingAndTruckingCompany()
      panel.checkBookingAndTruckingCompanyAllowedShippingLines()
      if (this.verifyBolValue) {
        panel.checkBolAndTruckingCompanyAllowedShippingLines()
      }
    })
  }

  #initialErrors () {
    return {
      presence: [],
      remote:   [], // list returned by API
    }
  }

  setPresenceError(msg) {
    if (this.errors.presence[0] !== msg) {
      this.errors.presence = [msg]
      this.#render()
    }
  }

  clearPresenceError() {
    if (this.errors.presence.length) {
      this.errors.presence = []
      this.#render()
    }
  }

  setRemoteErrors (list = []) {
    this.errors.remote = list
    this.#render()
  }

  clearErrors () {
    this.errors = this.#initialErrors()
    this.#render()
  }

  #render () {
    if (!this.hasErrorsTarget) return
    const messages = [
      ...this.errors.presence,
      ...this.errors.remote,
    ]

    this.errorsTarget.innerHTML = messages.length
      ? `<div class="alert alert-danger" role="alert">
           <ul style="padding:0 0 0 1em;">
             ${messages.map(m => `<li><p style="margin:0;">${m}</p></li>`).join("")}
           </ul>
         </div>`
      : ""
  }

  updateSelect2(selectTarget, value, text, selectText = null) {
    if (!selectTarget || !value || !text) return
    const option = new Option(selectText || text, value, true, true)
    if (selectText) option.dataset.selectText = selectText
    $(selectTarget).append(option).trigger('change.select2')
  }
}
